import { AxiosResponse, Method } from 'axios';
import { AddMultipleNewUserRequest } from 'components/universal/addMultipleUser';
import { AddNewUserRequest } from 'components/universal/addNewUser';
import { DeleteUsersValue } from 'components/universal/deleteUsersForm';
import { EditUserRequest } from 'components/universal/editUserDataForm';
import { sendLoginsAndPasswordsValue } from 'components/universal/generateLoginForm';
import {
    PasswordUpdateDTO,
    ResetPasswordDTO,
    SetNewPasswordDTO,
    SignInDTO,
    SignUpDTO,
} from 'types/user';
import { request } from 'utils/request';
import { UpdateUserRequest } from 'views/complete-profile';
import { Workplace } from 'views/employee-management/useEmployees';
import {
    Department,
    PaginatedResponse,
    WorkplaceType,
} from 'views/registration/useRegistrationHook';

export const signIn = async (userData: SignInDTO) =>
    await request('/auth/login', { method: 'POST' }, userData);

export const confirmRegistration = async (userData: SignInDTO) =>
    await request('/auth/confirm-registration', { method: 'POST' }, userData);
    
export const signUp = async (userData: SignUpDTO) =>
    await request('/auth/registration', { method: 'POST' }, userData);

export const addWorkplace = async (userData: SignUpDTO) =>
    await request('/auth/add-workplace', { method: 'POST' }, userData);

export const logout = async () =>
    await request('/auth/logout/current', { method: 'POST' });

export const passwordUpdate = async (passwordData: PasswordUpdateDTO) =>
    await request(`/auth/password-update`, { method: 'POST' }, passwordData);

export const sendEmailForPasswordReset = async (data: ResetPasswordDTO) =>
    await request(`/auth/password-reset/send-email`, { method: 'POST' }, data);

export const passwordReset = async (data: SetNewPasswordDTO) =>
    await request('/auth/password-reset', { method: 'POST' }, data);

export const getUserData = async (skipModal = false) => {
    const res = await request('/user/read', {});
    if (!skipModal) {
        window.dispatchEvent(new CustomEvent('userDataLoaded', { detail: res.data }));
    }
    return res;
};

export const getWorkplaces = async ({
    postcode,
    city,
    name,
    street,
    number,
    page = 1,
}: {
    postcode?: string;
    city?: string;
    name?: string;
    street?: string;
    number?: string;
    page?: number;
}): Promise<PaginatedResponse<WorkplaceType>> => {
    const params: Record<string, any> = { page };

    if (postcode) params.postcode = postcode;
    if (city) params.city = city;
    if (name) params.name = name;
    if (street) params.street = street;
    if (number) params.number = number;

    const payload = {
        method: 'GET' as Method,
        params,
    };

    const res = await request('/workplaces', payload);
    return res.data;
};

export const getUsers = async ({
    page,
    departmentIds,
    employeePositionIds,
    lastActivity,
    courseIds,
    withoutAnyCourseDone,
    workplaceId,
    search,
}: {
    page: number;
    departmentIds?: number[];
    employeePositionIds?: number[];
    lastActivity?: string;
    courseIds?: number[];
    withoutAnyCourseDone?: boolean;
    workplaceId?: number;
    search?: string;
}): Promise<{ data: any[]; total: number }> => {
    const params: Record<string, any> = { page };

    if (workplaceId) params.workplace_id = workplaceId;

    const payload = {
        method: 'GET' as Method,
        params,
        paramsSerializer: (p: Record<string, any>) => {
            const parts: string[] = [];
            Object.entries(p).forEach(([key, value]) => {
                if (Array.isArray(value)) {
                    value.forEach(v => parts.push(`${key}[]=${encodeURIComponent(v)}`));
                } else {
                    parts.push(`${key}=${encodeURIComponent(value)}`);
                }
            });
            return parts.join('&');
        },
    };

    if (departmentIds?.length) {
        params['department_ids'] = departmentIds;
    }

    if (employeePositionIds?.length) {
        params['employee_position_ids'] = employeePositionIds;
    }

    if (lastActivity) {
        params['last_activity'] = lastActivity;
    }

    if (courseIds?.length) {
        params['done_course_ids'] = courseIds;
    }

    if (withoutAnyCourseDone) {
        params['without_any_course_done'] = 1;
    }

    if (search) {
        params['search'] = search;
    }

    const res = await request(`/admin/users`, payload);
    return { data: res.data?.data ?? res.data, total: res.data?.total ?? 0 };
};

export const addNewUser = async (
    payload: AddNewUserRequest
): Promise<AxiosResponse<{ message: string }>> => {
    return await request(`/admin/users`, {
        method: 'POST',
        data: payload.data,
    });
};

export const editUser = async (
    payload: EditUserRequest
): Promise<AxiosResponse<{ message: string }>> => {
    return await request(`/admin/users/${payload.data['userIds']}`, {
        method: 'PUT',
        data: payload.data,
    });
};

export const generateLoginAndPasswordAxsios = async (
    payload: sendLoginsAndPasswordsValue
): Promise<AxiosResponse<Blob>> => {
    return await request(`/admin/credentials`, {
        method: 'POST',
        data: { ids: payload.userIds },
        responseType: 'blob',
    });
};

export const deleteUsersAxios = async (
    payload: DeleteUsersValue
): Promise<AxiosResponse<{ message: string }>> => {
    return await request(`/admin/users`, {
        method: 'DELETE',
        data: { ids: payload.ids },
    });
};

export const getUserWorkplaces = async (): Promise<Workplace[]> => {
    let page = 1;
    let lastPage = 1;
    let allWorkplaces: Workplace[] = [];

    do {
        const res = await request(`/admin/workplaces`, { method: 'GET', params: { page } });
        const data = res.data.data as Workplace[];
        allWorkplaces = [...allWorkplaces, ...data];
        lastPage = res.data.meta?.last_page ?? 1;
        page++;
    } while (page <= lastPage);

    return allWorkplaces;
};

export const acceptUserAxios = async (id : number): Promise<
    AxiosResponse<{ message: string }>
> => {
    const res = await request(`/admin/users/${id}/accept`, { method: 'PUT' });
    return res; 
};

export const addMultipleNewUser = async (
    payload: AddMultipleNewUserRequest
): Promise<AxiosResponse<Blob>> => {
    return await request(`/admin/mass-credentials`, {
        method: 'POST',
        data: payload.data,
        responseType: 'blob',
    });
};

export const fetchDepartments = async (workplaceId: number, page = 1): Promise<any> => {
    const res = await request(`/workplaces/${workplaceId}/departments`, {
        method: 'GET',
        params: { page },
    });
    return res.data;
};

export const fetchDepartmentsPaginated = async (workplaceId: number, page: number): Promise<PaginatedResponse<Department>> => {
    const res = await request(`/workplaces/${workplaceId}/departments`, { method: 'GET', params: { page } });
    return res.data;
};

export interface EmployeePosition {
    id: number;
    name: string;
}

export const getEmployeePositions = async (page: number, name?: string): Promise<PaginatedResponse<EmployeePosition>> => {
    const res = await request('/employee-positions', { method: 'GET', data: name ? { name } : undefined, params: { page } });
    return res.data;
};

export const createDepartment = async (workplaceId: number, name: string): Promise<any> => {
    const res = await request('/admin/departments', {
        method: 'POST',
        data: {
            name,
            workplace_id: workplaceId,
        },
    });
    return res.data;
};

export const updateDepartment = async (departmentId: number, name: string, workplaceId: number): Promise<any> => {
    const res = await request(`/admin/departments/${departmentId}`, {
        method: 'PUT',
        data: { name, workplace_id: workplaceId },
    });
    return res.data;
};

export const updateWorkplace = async (id: number, data: {
    workplace_name: string;
    workplace_address_city: string;
    workplace_address_street: string;
    workplace_address_number: string;
    workplace_address_postcode: string;
}): Promise<AxiosResponse<boolean>> => {
    return await request(`/admin/workplaces/${id}`, {
        method: 'PUT',
        data,
    });
};

export const updateUser = async (data: UpdateUserRequest) =>
    await request('/user/update', {
        method: 'POST',
        data,
});

export const exportUsersCsv = async ({
    departmentIds,
    lastActivity,
    courseIds,
    withoutAnyCourseDone,
    workplaceId,
    search,
}: {
    departmentIds?: number[];
    lastActivity?: string;
    courseIds?: number[];
    withoutAnyCourseDone?: boolean;
    workplaceId?: number;
    search?: string;
}): Promise<AxiosResponse<Blob>> => {
    const params: Record<string, any> = {};

    if (workplaceId) params.workplace_id = workplaceId;
    if (departmentIds?.length) params.department_ids = departmentIds;
    if (lastActivity) params.last_activity = lastActivity;
    if (courseIds?.length) params.done_course_ids = courseIds;
    if (withoutAnyCourseDone) params.without_any_course_done = 1;
    if (search) params.search = search;

    return await request('/admin/users/export-csv', {
        method: 'GET',
        responseType: 'blob',
        params,
        paramsSerializer: (p: Record<string, any>) => {
            const parts: string[] = [];
            Object.entries(p).forEach(([key, value]) => {
                if (Array.isArray(value)) {
                    value.forEach(v => parts.push(`${key}[]=${encodeURIComponent(v)}`));
                } else {
                    parts.push(`${key}=${encodeURIComponent(value)}`);
                }
            });
            return parts.join('&');
        },
    });
};